import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { fmt, METRICS } from '../../../data/metrics';
import type { DrillBasis } from '../../../types';
import { Icon } from '../../shared/Icon';
import { DrillBucketBarChart } from './DrillBucketBarChart';
import { DrillTile } from './DrillTile';
import { DrillAreaChart } from './charts/DrillAreaChart';

export function DrillTrendSection({
  metricKey,
  secondaryKey,
  series,
  secondarySeries,
  compareSeries,
  seriesLabel,
  secondarySeriesLabel,
  compareLabel,
  compareBucketLabels,
  bucketLabels,
  periodValue,
  periodLabel,
  trendPct,
  trendUnit,
  trendLabel,
  secondaryValue,
  secondaryTrendPct,
  secondaryTrendUnit,
  secondaryGoodHigh,
  fmtVal,
  fmtVal2,
  basis,
}: {
  metricKey: string;
  secondaryKey?: string;
  series: number[];
  secondarySeries?: number[];
  /** Compare-period series, aligned 1:1 with `series`. Rendered as a dashed overlay. */
  compareSeries?: number[];
  seriesLabel?: string;
  secondarySeriesLabel?: string;
  /** Legend label for the compare overlay, e.g. "vs 3 Jun – 30 Jun". */
  compareLabel?: string;
  /** The compare window of each point, aligned 1:1 with `compareSeries` ("29 Sept – 5 Oct 2025"). */
  compareBucketLabels?: string[];
  bucketLabels?: string[];
  /** Selected-frame total / rate — not the latest grain bucket. */
  periodValue: number;
  periodLabel?: string;
  /** Period-over-prior-period change, matching the toolbar. */
  trendPct?: number | null;
  /** '%' by default; 'pt' for rate metrics. */
  trendUnit?: string;
  /** What the change compares against, e.g. "vs prior 4 weeks". */
  trendLabel?: string;
  /** The paired metric's period figure and change (the dashed line). */
  secondaryValue?: number;
  secondaryTrendPct?: number | null;
  secondaryTrendUnit?: string;
  secondaryGoodHigh?: boolean;
  fmtVal: (v: number) => string;
  fmtVal2?: (v: number) => string;
  /** Reservations and guests behind the period figure (the tile's n). */
  basis?: DrillBasis;
}) {
  const goodHigh = METRICS[metricKey]?.better === 'high';
  const deltaGood =
    trendPct == null ? undefined : goodHigh ? trendPct >= 0 : trendPct <= 0;
  const rangeLabel =
    periodLabel ||
    (bucketLabels && bucketLabels.length > 1
      ? `${bucketLabels[0]} – ${bucketLabels[bucketLabels.length - 1]}`
      : bucketLabels?.[0]);
  const short = METRICS[metricKey]?.short ?? metricKey;
  const secondaryShort = secondaryKey
    ? (secondarySeriesLabel ?? METRICS[secondaryKey]?.short ?? secondaryKey)
    : undefined;
  const title = secondaryShort
    ? i18n.t('performance:drillUi.overTimePair', { primary: short, secondary: secondaryShort })
    : (seriesLabel ?? i18n.t('performance:drillUi.overTime', { label: short }));
  const hasCompare =
    !!compareSeries && compareSeries.length === series.length;
  const hasTrend = series.length >= 2;
  const hasSecondary = !!secondarySeries?.length && !!fmtVal2 && !!secondaryShort;
  const secondaryDeltaGood =
    secondaryTrendPct == null
      ? undefined
      : secondaryGoodHigh
        ? secondaryTrendPct >= 0
        : secondaryTrendPct <= 0;

  const inspector = useMemo(
    () => (
      <DrillBucketBarChart
        bare
        series={series}
        secondarySeries={secondarySeries}
        compareSeries={compareSeries}
        seriesLabel={seriesLabel}
        secondarySeriesLabel={secondarySeriesLabel}
        compareLabel={compareLabel}
        compareBucketLabels={compareBucketLabels}
        bucketLabels={bucketLabels}
        fmtVal={fmtVal}
        fmtVal2={fmtVal2}
        metricKey={metricKey}
        secondaryKey={secondaryKey}
      />
    ),
    [
      series,
      secondarySeries,
      compareSeries,
      seriesLabel,
      secondarySeriesLabel,
      compareLabel,
      compareBucketLabels,
      bucketLabels,
      fmtVal,
      fmtVal2,
      metricKey,
      secondaryKey,
    ],
  );

  return (
    <DrillTile
      sectionId="trend"
      span={8}
      title={title}
      meta={
        hasCompare && compareLabel
          ? rangeLabel
            ? `${rangeLabel} · ${compareLabel}`
            : compareLabel
          : rangeLabel
      }
      inspector={hasTrend ? inspector : undefined}
      n={
        basis
          ? i18n.t('performance:drillUi.nBasis', {
              reservations: fmt.num(basis.reservations),
              guests: fmt.num(basis.guests),
            })
          : undefined
      }
    >
      <div className="ddrill-stat-inline">
        <strong>{fmtVal(periodValue)}</strong>
        <span>{short}</span>
        {trendPct != null && deltaGood != null ? (
          // The value stays ink; good / bad lives on the delta beside it.
          <span
            className="ddrill-delta"
            style={{ color: deltaGood ? 'var(--st-steady)' : 'var(--st-alert)' }}
            title={trendLabel}
          >
            <Icon name={trendPct >= 0 ? 'arrowUp' : 'arrowDown'} size={12} stroke={2.4} />
            {Math.abs(trendPct)}
            {trendUnit ?? '%'}
            {trendLabel ? ` · ${trendLabel}` : ''}
          </span>
        ) : null}
        {hasSecondary && secondaryValue != null ? (
          <span className="ddrill-stat-inline-2" data-testid="trend-secondary-stat">
            <strong>{fmtVal2!(secondaryValue)}</strong>
            <span>{secondaryShort}</span>
            {secondaryTrendPct != null && secondaryDeltaGood != null ? (
              <span
                className="ddrill-delta"
                style={{ color: secondaryDeltaGood ? 'var(--st-steady)' : 'var(--st-alert)' }}
                title={trendLabel}
              >
                <Icon
                  name={secondaryTrendPct >= 0 ? 'arrowUp' : 'arrowDown'}
                  size={12}
                  stroke={2.4}
                />
                {Math.abs(secondaryTrendPct)}
                {secondaryTrendUnit ?? '%'}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
      {hasTrend ? (
        <DrillAreaChart
          series={series}
          secondarySeries={secondarySeries}
          compareSeries={compareSeries}
          labels={bucketLabels}
          fmtVal={fmtVal}
          fmtVal2={fmtVal2}
          primaryLabel={short}
          compareLabel={compareLabel}
          compareLabels={compareBucketLabels}
          // The legend's name for the dashed line, so tooltip and legend agree.
          secondaryLabel={secondaryShort}
          height={168}
        />
      ) : (
        <p className="ddrill-tile-note">{i18n.t('performance:drillUi.oneBucket')}</p>
      )}
      {hasTrend && (hasSecondary || (hasCompare && compareLabel)) ? (
        <div className="ddrill-legend" data-testid="trend-legend">
          <span className="ddrill-legend-item">
            <i className="ddrill-legend-swatch is-primary" />
            {short}
          </span>
          {hasSecondary ? (
            <span className="ddrill-legend-item">
              <i className="ddrill-legend-swatch is-secondary" />
              {secondaryShort}
            </span>
          ) : null}
          {hasCompare && compareLabel ? (
            <span className="ddrill-legend-item">
              <i className="ddrill-legend-swatch is-compare" />
              {compareLabel}
            </span>
          ) : null}
        </div>
      ) : null}
    </DrillTile>
  );
}
